import Color from 'color'
import { isSameSecond, setHours, setMinutes } from 'date-fns'
import { compose } from 'lodash/fp'
import { createSelector } from 'reselect'
import { groupBy, isEmpty, map, mapValues, memoize } from 'lodash'
import { CountryCode } from '../../../i18n/countryCodes'
import { EventStatus, IEvent } from '../../../models/Event'
import { labels } from '../../../models/Event/eventConfig'
import mapLabelFor from '../../../reducers/userObject/tags/mapLabelFor'
import { getCurrency } from '../../currency/selectors/currency'
import { IStore } from '../../index'
import {
  getCountryDict,
  getProfileObject,
} from '../../userObject/selectors/object'
import {
  getCalendarPreferences,
  getReservationLabelPattern,
  getUserSettings,
} from '../../userObject/selectors/settings'
import {
  tagsForEvents,
  tagsForEventsAdditional,
  tagsForEventsWithPreferences,
  tagsForProfile,
  tagsForRooms,
} from '../../userObject/tags'
import { getRooms } from './erhaltenZimmer'
import { getUpdateModalReservationId } from './getModalOpen'

export const getEvents = createSelector(
  // Input selector
  (state: IStore) => state.events.events,

  // Result function
  (events: IEvent[]) => {
    if (isEmpty(events)) {
      return []
    }

    const seenIds = new Set()
    return events.filter(event => {
      if (seenIds.has(event.id)) {
        return false
      }
      seenIds.add(event.id)
      return true
    })
  },
)

export const getSortedEvents = createSelector(
  [getEvents],
  events => events.sort((a, b) => +a.startDate - +b.startDate || +a.id - +b.id),
)

export function getEvent(state: IStore, props: { event: IEvent }) {
  return props.event
}

export function getEventById(id) {
  return function(state) {
    return (
      state.events.events.find(event => String(event.id) === String(id)) || null
    )
  }
}

export const getUpdateReservationFormEvent = createSelector(
  [getEvents, getUpdateModalReservationId],
  (events, id) => {
    return events ? events.find(e => e.id === id) : undefined
  },
)

export const getFromGroupReservations = createSelector(
  [getUpdateReservationFormEvent, getEvents],
  (event, events) => {
    return events.filter(
      e =>
        String(e.from_website) !== '0' &&
        isSameSecond(e.createdAt, event.createdAt),
    )
  },
)

export const withoutRemoved = createSelector(
  [getSortedEvents],
  events => events.filter(e => !e.isDeleted),
)

export const withoutRemovedRooms = createSelector(
  [getRooms],
  rooms => rooms.filter(r => !r.isDeleted),
)

// Apparently, after removing rooms, api does not immediately notify about removed events
export const notInRemovedAndRemovedRooms = createSelector(
  [withoutRemoved, withoutRemovedRooms],
  (events, rooms) => {
    if (!rooms.length) return events
    const roomIds = map(rooms, 'id')
    return events.filter(e => roomIds.includes(e.roomId))
  },
)

export const notRemovedAndUnavailableEvents = createSelector(
  [notInRemovedAndRemovedRooms],
  events => events.filter(e => e.stage !== EventStatus.Unavailable),
)

export const getReservationCustomStatusColor = id => {
  return createSelector(
    [getEvents, getCalendarPreferences],
    (events, settings) =>
      settings.colors[
        (events.find(e => String(e.id) === String(id)) &&
          events.find(e => String(e.id) === String(id)).stage) ||
          EventStatus.NoPayment
      ].toString(),
  )
}

export const withoutCancelled = createSelector(
  [notInRemovedAndRemovedRooms],
  events => events.filter(e => e.stage !== EventStatus.Canceled),
)

export const withoutCancelledAndUnavailable = createSelector(
  [withoutCancelled],
  events => events.filter(e => e.stage !== EventStatus.Unavailable),
)

//Sorting moved to reducer
export const withoutCancelledSortedByStartDate = withoutCancelled

export const findEventsForOpenModal = id =>
  createSelector(
    [notInRemovedAndRemovedRooms],
    events => events.filter(event => event.roomId === id),
  )

export const doLabel = createSelector(
  [
    withoutRemovedRooms,
    getProfileObject,
    getCountryDict,
    getCurrency,
    getReservationLabelPattern,
    getCalendarPreferences,
  ],
  (rooms, profile, countries, currencies, userSettings, preferences) => {
    const preferredArrival =
      preferences && preferences.daynightFrom
        ? setMinutes(
            setHours(
              new Date(),
              Number(preferences.daynightFrom.split(':')[0]),
            ),
            Number(preferences.daynightFrom.split(':')[1]),
          )
        : ''
    const preferredDeparture =
      preferences && preferences.daynightTo
        ? setMinutes(
            setHours(new Date(), Number(preferences.daynightTo.split(':')[0])),
            Number(preferences.daynightTo.split(':')[1]),
          )
        : ''

    return event => {
      try {
        const room = rooms.find(r => r.id === event.roomId)
        return compose(
          mapLabelFor(
            {
              ...event,
              format: userSettings.format,
              formatDate: userSettings.formatDate,
              formatHours: userSettings.formatHours,
              preferredArrival,
              preferredDeparture,
            },
            tagsForEvents,
          ),
          mapLabelFor({ ...profile, countries, currencies }, tagsForProfile),
          mapLabelFor(
            {
              ...room,
              format: userSettings.format,
              formatDate: userSettings.formatDate,
              formatHours: userSettings.formatHours,
            },
            tagsForRooms,
          ),
          mapLabelFor(
            { ...event, settings: preferences },
            tagsForEventsWithPreferences,
          ),
        )(userSettings.pattern)
      } catch (e) {
        return event.guest && `${event.guest.name}*`
      }
    }
  },
)

export const getEventsWithLabel = withoutCancelledSortedByStartDate

export const getEventsWithLabelGroupedByRoom = createSelector(
  [getEventsWithLabel],
  events => {
    return groupBy(events, 'roomId')
  },
)

export const mapEmailTemplateLabels = createSelector(
  [
    (_, props: { event: IEvent }) => props.event,
    withoutRemovedRooms,
    getProfileObject,
    getCountryDict,
    getCurrency,
    getReservationLabelPattern,
    getCalendarPreferences,
  ],
  (event, rooms, profile, countries, currencies, userSettings, preferences) => {
    const room = event.roomId ? rooms.find(r => r.id === event.roomId) : null

    const preferredArrival = setMinutes(
      setHours(new Date(), Number(preferences.daynightFrom.split(':')[0])),
      Number(preferences.daynightFrom.split(':')[1]),
    )
    const preferredDeparture = setMinutes(
      setHours(new Date(), Number(preferences.daynightTo.split(':')[0])),
      Number(preferences.daynightTo.split(':')[1]),
    )

    ;

    return memoize((language: CountryCode) =>
      compose(
        mapLabelFor(
          {
            ...event,
            countries,
            format: userSettings.format,
            formatDate: userSettings.formatDate,
            formatHours: userSettings.formatHours,
            language,
            preferredArrival,
            preferredDeparture,
          },
          tagsForEvents,
        ),
        mapLabelFor(
          {
            ...event,
            countries,
            format: userSettings.format,
            language,
            formatDate: userSettings.formatDate,
            formatHours: userSettings.formatHours,
          },
          tagsForEventsAdditional,
        ),
        mapLabelFor({ ...profile, countries, language, currencies }, tagsForProfile),
        mapLabelFor(
          {
            ...room,
            format: userSettings.format,
            formatDate: userSettings.formatDate,
            formatHours: userSettings.formatHours,
            language,
          },
          tagsForRooms,
        ),
        mapLabelFor(
          { ...event, language, settings: preferences },
          tagsForEventsWithPreferences,
        ),
      ),
    )
  },
)

export const eventStatus = createSelector(
  [getUserSettings],
  settings =>
    memoize((event: IEvent) => {
      function colorToString(color: Color) {
        return color.rgb().toString()
      }

      const status = {
        color: 'white',
        label: 'placeholder',
      }

      if (settings && event.stage) {
        status.color = colorToString(
          settings.customReservationStatus.colors[event.stage],
        )
        status.label = settings.customReservationStatus.names[event.stage]
      } else if (settings && settings.defaultReservationStatus) {
        status.color = colorToString(
          settings.customReservationStatus.colors[
            settings.defaultReservationStatus
          ],
        )
        status.label =
          settings.customReservationStatus.names[
            settings.defaultReservationStatus
          ]
      }
      if (event.customColor) {
        status.color = colorToString(event.customColor)
      }

      return status
    }),
)

export function getEventsByRoomId(id: number) {
  return createSelector(
    [withoutCancelled],
    events => events.filter(event => Number(event.roomId) === id),
  )
}

export const getReservationStatusSettingsForEventId = createSelector(
  [
    getCalendarPreferences,
    getUserSettings,
    (state, props) => getEventById(props.id)(state),
  ],
  (preferences, settings, event) => {
    return {
      colors: preferences.colors,
      labels: mapValues(preferences.labels, (v, k: EventStatus) => {
        if (v === '') {
          if (k === EventStatus.Advance) {
            const prepaidType = event
              ? event.prepaidType
              : settings.defaultPrepaidType

            return Number(prepaidType) === 0 ? labels.get('3-2') : labels.get(k)
          }

          return labels.get(k)
        }

        return v
      }),
    }
  },
)

export const getReservationStatusSettingsForEvent = createSelector(
  [
    getCalendarPreferences,
    getUserSettings,
    (_, props: { event: IEvent }) => props.event,
  ],
  (preferences, settings, event) => {
    return {
      colors: preferences.colors,
      labels: mapValues(preferences.labels, (v, k: EventStatus) => {
        if (v === '') {
          if (k === EventStatus.Advance) {
            const prepaidType = event
              ? event.prepaidType
              : settings.defaultPrepaidType

            return Number(prepaidType) === 0 ? labels.get('3-2') : labels.get(k)
          }

          return labels.get(k)
        }

        return v
      }),
    }
  },
)

export const getEventLabels = (state: IStore) =>
  state.bookingCalendar.eventLabels
